import React, { useState, useEffect } from 'react';
import '../css/studentCreate.css';
import {
  FaUser,
  FaIdCard,
  FaGraduationCap,
  FaPhone,
  FaCalendarAlt,
  FaBook,
  FaSchool,
  FaSave,
  FaTimes,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { schoolAPI, classAPI } from '../../../../utils/api';

function StudentCreate({ student = null, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    b_form_no: '',
    student_name: '',
    father_name: '',
    gender: '',
    dob: '',
    religion: '',
    class: '',
    // course_type removed
    // selected_books removed
    semis_code: '',
    disability_status: 'Not Disabled',
    student_contact: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    // Load schools and classes from backend
    (async () => {
      try {
        const [sRes, cRes] = await Promise.all([schoolAPI.getAll(), classAPI.getAll()]);
        const list = (sRes.data || []).map(s => ({
          semis_code: s.semis_code || s.semisCode || s.code || '',
          name: s.school_name || s.name || '',
        })).filter(s => s.semis_code);
        setSchools(list);
        setClasses((cRes.data || []).map(c => ({ id: c.id, name: c.name })));
      } catch (e) {
        // keep empty, user can still type
      }
    })();

    if (student) {
      setFormData({
        b_form_no: student.b_form_no || '',
        student_name: student.student_name || '',
        father_name: student.father_name || '',
        gender: student.gender || '',
        dob: student.dob || '',
        religion: student.religion || '',
        class: student.class || '',
        semis_code: student.semis_code || '',
        disability_status: student.disability_status || 'Not Disabled',
        student_contact: student.student_contact || '',
      });
    }
  }, [student]);

  const validateForm = () => {
    const newErrors = {};

    // B-Form presence only (no format restriction)
    if (!formData.b_form_no.trim()) {
      newErrors.b_form_no = 'B-Form Number is required';
    }

    // Student Name validation
    if (!formData.student_name.trim()) {
      newErrors.student_name = 'Student Name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.student_name.trim())) {
      newErrors.student_name = 'Student Name should contain only letters';
    } else if (formData.student_name.trim().length < 2) {
      newErrors.student_name = 'Student Name must be at least 2 characters';
    }

    // Father's Name validation
    if (!formData.father_name.trim()) {
      newErrors.father_name = "Father's Name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.father_name.trim())) {
      newErrors.father_name = "Father's Name should contain only letters";
    } else if (formData.father_name.trim().length < 2) {
      newErrors.father_name = "Father's Name must be at least 2 characters";
    }

    // Gender validation
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Date of Birth validation
    if (!formData.dob) {
      newErrors.dob = 'Date of Birth is required';
    }

    // Class validation
    if (!formData.class) {
      newErrors.class = 'Class is required';
    }

    // course_type and selected_books removed

    // SEMIS Code validation
    if (!formData.semis_code) {
      newErrors.semis_code = 'SEMIS Code is required';
    }

    // Contact validation (optional but if provided, must be valid)
    if (
      formData.student_contact &&
      !/^03\d{9}$/.test(formData.student_contact)
    ) {
      newErrors.student_contact =
        'Contact number must be a valid Pakistani mobile number (03XXXXXXXXX)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }

    // no dependent resets needed
  };

  // removed book selection handling

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fix the errors in the form',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(formData);
      }
      Swal.fire({
        title: 'Success',
        text: student ? 'Student updated successfully!' : 'Student created successfully!',
        icon: 'success',
        confirmButtonColor: '#4CAF50',
      });
      onClose && onClose();
    } catch (error) {
      const msg = error?.response?.data?.error || error.message || 'An error occurred. Please try again.';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonColor: '#f44336' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // removed available books helper

  return (
    <div className="eduss-student-create">
      <form onSubmit={handleSubmit} className="eduss-student-create-form">
        <div className="eduss-student-create-section">
          <h3>
            <FaUser /> Basic Information
          </h3>

          <div className="eduss-student-create-row">
            <div className="eduss-student-create-field">
              <label htmlFor="b_form_no">
                <FaIdCard /> B-Form Number *
              </label>
              <input
                type="text"
                id="b_form_no"
                name="b_form_no"
                value={formData.b_form_no}
                onChange={handleInputChange}
                placeholder="Enter 13-digit B-Form number"
                maxLength="13"
                className={errors.b_form_no ? 'error' : ''}
              />
              {errors.b_form_no && (
                <span className="eduss-student-create-error">
                  {errors.b_form_no}
                </span>
              )}
            </div>

            <div className="eduss-student-create-field">
              <label htmlFor="student_name">
                <FaUser /> Student Name *
              </label>
              <input
                type="text"
                id="student_name"
                name="student_name"
                value={formData.student_name}
                onChange={handleInputChange}
                placeholder="Enter full name"
                className={errors.student_name ? 'error' : ''}
              />
              {errors.student_name && (
                <span className="eduss-student-create-error">
                  {errors.student_name}
                </span>
              )}
            </div>
          </div>

          <div className="eduss-student-create-row">
            <div className="eduss-student-create-field">
              <label htmlFor="father_name">
                <FaUser /> Father&apos;s Name *
              </label>
              <input
                type="text"
                id="father_name"
                name="father_name"
                value={formData.father_name}
                onChange={handleInputChange}
                placeholder="Enter father's full name"
                className={errors.father_name ? 'error' : ''}
              />
              {errors.father_name && (
                <span className="eduss-student-create-error">
                  {errors.father_name}
                </span>
              )}
            </div>

            <div className="eduss-student-create-field">
              <label>
                <FaUser /> Gender *
              </label>
              <div className="eduss-student-create-radio-group">
                <label className="eduss-student-create-radio">
                  <input
                    type="radio"
                    name="gender"
                    value="M"
                    checked={formData.gender === 'M'}
                    onChange={handleInputChange}
                  />
                  <span>Male</span>
                </label>
                <label className="eduss-student-create-radio">
                  <input
                    type="radio"
                    name="gender"
                    value="F"
                    checked={formData.gender === 'F'}
                    onChange={handleInputChange}
                  />
                  <span>Female</span>
                </label>
              </div>
              {errors.gender && (
                <span className="eduss-student-create-error">
                  {errors.gender}
                </span>
              )}
            </div>
          </div>

          <div className="eduss-student-create-row">
            <div className="eduss-student-create-field">
              <label htmlFor="dob">
                <FaCalendarAlt /> Date of Birth *
              </label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className={errors.dob ? 'error' : ''}
              />
              {errors.dob && (
                <span className="eduss-student-create-error">{errors.dob}</span>
              )}
            </div>

            <div className="eduss-student-create-field">
              <label htmlFor="religion">
                <FaUser /> Religion
              </label>
              <select
                id="religion"
                name="religion"
                value={formData.religion}
                onChange={handleInputChange}
              >
                <option value="">Select Religion (optional)</option>
                <option value="Islam">Islam</option>
                <option value="Hinduism">Hinduism</option>
                <option value="Christianity">Christianity</option>
                <option value="Buddhism">Buddhism</option>
                <option value="Sikhism">Sikhism</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div className="eduss-student-create-section">
          <h3>
            <FaGraduationCap /> Academic Information
          </h3>

          <div className="eduss-student-create-row">
            <div className="eduss-student-create-field">
              <label htmlFor="class">
                <FaGraduationCap /> Class *
              </label>
              <select
                id="class"
                name="class"
                value={formData.class}
                onChange={handleInputChange}
                className={errors.class ? 'error' : ''}
              >
                <option value="">Select Class</option>
                {classes.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
              {errors.class && (
                <span className="eduss-student-create-error">
                  {errors.class}
                </span>
              )}
            </div>
          </div>

          {/* Books removed */}

          <div className="eduss-student-create-field">
            <label htmlFor="semis_code">
              <FaSchool /> SEMIS Code *
            </label>
            <select
              id="semis_code"
              name="semis_code"
              value={formData.semis_code}
              onChange={handleInputChange}
              className={errors.semis_code ? 'error' : ''}
            >
              <option value="">Select School</option>
              {schools.length === 0 ? (
                <option value="" disabled>(No schools loaded)</option>
              ) : (
                schools.map(school => (
                  <option key={school.semis_code} value={school.semis_code}>
                    {school.semis_code} - {school.name}
                  </option>
                ))
              )}
            </select>
            {errors.semis_code && (
              <span className="eduss-student-create-error">
                {errors.semis_code}
              </span>
            )}
          </div>
        </div>

        <div className="eduss-student-create-section">
          <h3>
            <FaUser /> Additional Information
          </h3>

          <div className="eduss-student-create-row">
            <div className="eduss-student-create-field">
              <label htmlFor="disability_status">
                <FaUser /> Disability Status
              </label>
              <select
                id="disability_status"
                name="disability_status"
                value={formData.disability_status}
                onChange={handleInputChange}
              >
                <option value="Not Disabled">Not Disabled</option>
                <option value="Disabled">Disabled</option>
              </select>
            </div>

            <div className="eduss-student-create-field">
              <label htmlFor="student_contact">
                <FaPhone /> Contact Number
              </label>
              <input
                type="tel"
                id="student_contact"
                name="student_contact"
                value={formData.student_contact}
                onChange={handleInputChange}
                placeholder="03XXXXXXXXX (optional)"
                className={errors.student_contact ? 'error' : ''}
              />
              {errors.student_contact && (
                <span className="eduss-student-create-error">
                  {errors.student_contact}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="eduss-student-create-actions">
          <button
            type="button"
            onClick={onClose}
            className="eduss-student-create-btn cancel"
            disabled={isSubmitting}
          >
            <FaTimes /> Cancel
          </button>
          <button
            type="submit"
            className="eduss-student-create-btn submit"
            disabled={isSubmitting}
          >
            <FaSave />{' '}
            {isSubmitting
              ? 'Saving...'
              : student
                ? 'Update Student'
                : 'Create Student'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default StudentCreate;
